import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Tables, TablesUpdate } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { queryKeys } from "./keys";

export type Dossier = Tables<"dossiers">;
export type DossierUpdate = TablesUpdate<"dossiers">;
export type DossierDocument = Tables<"dossier_documents">;
export type DossierScenario = Tables<"dossier_scenarios">;
export type DossierStatusHistoryEntry = Tables<"dossier_status_history">;
export type DossierHypotheses = Tables<"dossier_hypotheses">;
// Type local — sera remplacé par Tables<"dossier_career_periods"> dès que la
// migration 20260520120033 sera poussée et les types régénérés.
export interface DossierCareerPeriod {
  id: string;
  dossier_id: string;
  ordre: number;
  date_debut: string;
  date_fin: string | null;
  code_profession: string | null;
  profession: string | null;
  type_employeur: string | null;
  code_convention: string | null;
  libelle_convention: string | null;
  type_revenus: string;
  periodicite_revenus: string;
  devise_revenus: string;
  profil_evolution: string | null;
  taux_evolution: number | null;
  revenu_global: number | null;
  revenus_annuels: unknown;
  indicateurs: unknown;
  source: string;
  notes: string | null;
  is_validated: boolean;
  validated_at: string | null;
  validated_by: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export type DossierWithRelations = Dossier & {
  operation: Pick<
    Tables<"operations">,
    | "id"
    | "nom"
    | "slug"
    | "annee"
    | "bri_type_default"
    | "societe_id"
    | "parcours_type"
    | "chomage_scenario_enabled"
    | "rdv_duration_minutes"
  > | null;
  consultant: Pick<
    Tables<"profiles">,
    "user_id" | "prenom" | "nom" | "email" | "avatar_url"
  > | null;
  chef_projet: Pick<Tables<"profiles">, "user_id" | "prenom" | "nom" | "email"> | null;
  /** Consultant de RELECTURE (B6) : second consultant, habilité à valider N2. */
  reviewer: Pick<Tables<"profiles">, "user_id" | "prenom" | "nom" | "email" | "avatar_url"> | null;
  /** Consultant de RESTITUTION (P1) : null = identique au consultant de production. */
  restitution_consultant: Pick<
    Tables<"profiles">,
    "user_id" | "prenom" | "nom" | "email" | "avatar_url"
  > | null;
};

// P1 : le helper pur vit dans src/lib/app/restitution-consultant.ts (sans
// import Supabase, testable hors navigateur) ; réexporté ici pour confort.
export { effectiveRestitutionConsultant } from "@/lib/app/restitution-consultant";

const DOSSIER_WITH_RELATIONS_SELECT = `
  *,
  operation:operations!dossiers_operation_id_fkey (
    id, nom, slug, annee, bri_type_default, societe_id,
    parcours_type, chomage_scenario_enabled, rdv_duration_minutes
  ),
  consultant:profiles!dossiers_assigned_consultant_id_fkey (
    user_id, prenom, nom, email, avatar_url
  ),
  chef_projet:profiles!dossiers_assigned_chef_projet_id_fkey (
    user_id, prenom, nom, email
  ),
  reviewer:profiles!dossiers_assigned_reviewer_id_fkey (
    user_id, prenom, nom, email, avatar_url
  ),
  restitution_consultant:profiles!dossiers_assigned_restitution_consultant_id_fkey (
    user_id, prenom, nom, email, avatar_url
  )
`;

/**
 * Hook : dossier du salarié connecté.
 *
 * Trouve la fiche salarié via auth_user_id puis remonte le dossier le plus
 * récent (latest cycle_no). Renvoie null tant qu'il n'existe pas.
 */
export function useMyDossier() {
  const { effectiveUserId } = useAuth();
  return useQuery({
    queryKey: effectiveUserId ? queryKeys.dossier.mine(effectiveUserId) : ["dossier", "anonymous"],
    queryFn: async (): Promise<DossierWithRelations | null> => {
      if (!effectiveUserId) return null;

      const { data: salarie, error: salarieError } = await supabase
        .from("salaries")
        .select("id")
        .eq("auth_user_id", effectiveUserId)
        .maybeSingle();
      if (salarieError) throw salarieError;
      if (!salarie) return null;

      const { data, error } = await supabase
        .from("dossiers")
        .select(DOSSIER_WITH_RELATIONS_SELECT)
        .eq("salarie_id", salarie.id)
        .order("cycle_no", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as DossierWithRelations | null;
    },
    enabled: Boolean(effectiveUserId),
  });
}

/**
 * Hook : crée (ou récupère) le dossier du salarié connecté de façon idempotente.
 *
 * S'appuie sur `app.ensure_my_dossier()` (SECURITY DEFINER) qui agit sur la
 * fiche liée à `auth.uid()`. Depuis l'impersonation RÉELLE (vraie session
 * Supabase de la cible), `auth.uid()` EST le salarié impersoné : le même
 * chemin fonctionne donc dans tous les cas. (L'ancien branchement vers
 * `ensure_dossier_for_user`, réservé admin, échouait en impersonation puisque
 * la session courante n'est plus celle de l'admin — écran vide « dossier pas
 * prêt », retour client du 31/07.)
 */
export function useEnsureMyDossier() {
  const queryClient = useQueryClient();
  const { effectiveUserId } = useAuth();
  return useMutation({
    mutationFn: async (): Promise<string | null> => {
      // Appel direct sur le client pour conserver le binding `this`
      // (sinon supabase-js lève « Cannot read properties of undefined (reading 'rest') »).
      const client = supabase as unknown as {
        rpc: (
          fn: string,
          args?: Record<string, unknown>,
        ) => Promise<{ data: string | null; error: { message: string } | null }>;
      };
      const { data, error } = await client.rpc("ensure_my_dossier");
      if (error) throw new Error(error.message);
      return data ?? null;
    },
    onSuccess: () => {
      if (effectiveUserId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.dossier.mine(effectiveUserId),
        });
        // La RPC peut créer une fiche salarié « libre » à la volée : on
        // rafraîchit aussi la fiche pour que le parcours s'affiche.
        void queryClient.invalidateQueries({
          queryKey: queryKeys.salarie.mine(effectiveUserId),
        });
      }
    },
  });
}

/**
 * Hook : récupère (ou crée) le dossier d'un salarié CIBLE, de façon idempotente.
 *
 * Réservé au staff (admin / chef de projet / consultant / DRH) DANS SON
 * PÉRIMÈTRE — la RPC `ensure_dossier_for_salarie` vérifie l'accès via
 * `app.has_access_to_salarie`. Sert à la prise en charge du parcours d'un
 * salarié depuis les espaces Admin / DRH / AON.
 */
export function useEnsureDossierForSalarie() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (salarieId: string): Promise<string | null> => {
      const client = supabase as unknown as {
        rpc: (
          fn: string,
          args?: Record<string, unknown>,
        ) => Promise<{ data: string | null; error: { message: string } | null }>;
      };
      const { data, error } = await client.rpc("ensure_dossier_for_salarie", {
        p_salarie_id: salarieId,
      });
      if (error) throw new Error(error.message);
      return data ?? null;
    },
    onSuccess: (_data, salarieId) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "salaries", "detail", salarieId, "dossier"],
      });
    },
  });
}

/**
 * Hook : dossier par id (Aon interne / DRH).
 */
export function useDossier(dossierId: string | undefined) {
  return useQuery({
    queryKey: dossierId ? queryKeys.dossier.byId(dossierId) : ["dossier", "none"],
    queryFn: async (): Promise<DossierWithRelations | null> => {
      if (!dossierId) return null;
      const { data, error } = await supabase
        .from("dossiers")
        .select(DOSSIER_WITH_RELATIONS_SELECT)
        .eq("id", dossierId)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as DossierWithRelations | null;
    },
    enabled: Boolean(dossierId),
  });
}

/**
 * Hook : documents d'un dossier.
 */
export function useDossierDocuments(dossierId: string | undefined) {
  return useQuery({
    queryKey: dossierId ? queryKeys.dossier.documents(dossierId) : ["dossier", "none", "documents"],
    queryFn: async (): Promise<DossierDocument[]> => {
      if (!dossierId) return [];
      const { data, error } = await supabase
        .from("dossier_documents")
        .select("*")
        .eq("dossier_id", dossierId)
        .order("uploaded_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: Boolean(dossierId),
  });
}

/**
 * Hook : scénarios sélectionnés sur un dossier.
 */
export function useDossierScenarios(dossierId: string | undefined) {
  return useQuery({
    queryKey: dossierId ? queryKeys.dossier.scenarios(dossierId) : ["dossier", "none", "scenarios"],
    queryFn: async (): Promise<DossierScenario[]> => {
      if (!dossierId) return [];
      const { data, error } = await supabase
        .from("dossier_scenarios")
        .select("*")
        .eq("dossier_id", dossierId)
        .order("kind");
      if (error) throw error;
      return data ?? [];
    },
    enabled: Boolean(dossierId),
  });
}

/**
 * Hook : historique des changements de statut d'un dossier.
 */
export function useDossierStatusHistory(dossierId: string | undefined) {
  return useQuery({
    queryKey: dossierId
      ? queryKeys.dossier.statusHistory(dossierId)
      : ["dossier", "none", "status-history"],
    queryFn: async (): Promise<DossierStatusHistoryEntry[]> => {
      if (!dossierId) return [];
      const { data, error } = await supabase
        .from("dossier_status_history")
        .select("*")
        .eq("dossier_id", dossierId)
        .order("changed_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: Boolean(dossierId),
  });
}

/**
 * Hook : hypothèses de calcul d'un dossier.
 */
export function useDossierHypotheses(dossierId: string | undefined) {
  return useQuery({
    queryKey: dossierId
      ? queryKeys.dossier.hypotheses(dossierId)
      : ["dossier", "none", "hypotheses"],
    queryFn: async (): Promise<DossierHypotheses | null> => {
      if (!dossierId) return null;
      const { data, error } = await supabase
        .from("dossier_hypotheses")
        .select("*")
        .eq("dossier_id", dossierId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: Boolean(dossierId),
  });
}

/**
 * Hook : périodes de carrière d'un dossier (lecture).
 * NB : tant que la migration 20260520120033 n'est pas poussée, cette query
 * retournera 404 / une erreur Postgres. Le composant doit gérer ce cas.
 */
export function useDossierCareerPeriods(dossierId: string | undefined) {
  return useQuery({
    queryKey: dossierId
      ? (["dossier", dossierId, "career-periods"] as const)
      : (["dossier", "none", "career-periods"] as const),
    queryFn: async (): Promise<DossierCareerPeriod[]> => {
      if (!dossierId) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = supabase.from.bind(supabase) as unknown as (name: string) => any;
      const { data, error } = await client("dossier_career_periods")
        .select("*")
        .eq("dossier_id", dossierId)
        .order("ordre", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DossierCareerPeriod[];
    },
    enabled: Boolean(dossierId),
  });
}

// =============================================================================
// Mutations : périodes de carrière (CRUD côté production Aon)
// =============================================================================
// `payload` est typé large (Record) car la table n'est pas encore dans les
// types Supabase générés. Sera nettoyé après régénération.
type CareerPeriodPayload = Record<string, unknown>;

/**
 * Mutation : créer une période de carrière.
 * L'`ordre` est calculé automatiquement (max + 1) côté serveur via le
 * paramètre passé. Le créateur est l'utilisateur authentifié.
 */
export function useCreateDossierCareerPeriod() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (args: { dossierId: string; ordre: number; values: CareerPeriodPayload }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = supabase.from.bind(supabase) as unknown as (name: string) => any;
      const { data, error } = await client("dossier_career_periods")
        .insert({
          dossier_id: args.dossierId,
          ordre: args.ordre,
          created_by: user?.id ?? null,
          ...args.values,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data as DossierCareerPeriod;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["dossier", vars.dossierId, "career-periods"],
      });
    },
  });
}

/**
 * Mutation : modifier une période existante.
 */
export function useUpdateDossierCareerPeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      dossierId: string;
      periodId: string;
      values: CareerPeriodPayload;
    }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = supabase.from.bind(supabase) as unknown as (name: string) => any;
      const { data, error } = await client("dossier_career_periods")
        .update(args.values)
        .eq("id", args.periodId)
        .select("*")
        .single();
      if (error) throw error;
      return data as DossierCareerPeriod;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["dossier", vars.dossierId, "career-periods"],
      });
    },
  });
}

/**
 * Mutation : supprimer une période. Les ordres restants ne sont pas
 * réindexés (PK + index unique sur (dossier_id, ordre) → on accepte
 * les trous).
 */
export function useDeleteDossierCareerPeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { dossierId: string; periodId: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = supabase.from.bind(supabase) as unknown as (name: string) => any;
      const { error } = await client("dossier_career_periods").delete().eq("id", args.periodId);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["dossier", vars.dossierId, "career-periods"],
      });
    },
  });
}

/**
 * Mutation : valider / invalider une période (contrôle qualité).
 */
export function useValidateDossierCareerPeriod() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (args: { dossierId: string; periodId: string; isValidated: boolean }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = supabase.from.bind(supabase) as unknown as (name: string) => any;
      const { error } = await client("dossier_career_periods")
        .update({
          is_validated: args.isValidated,
          validated_at: args.isValidated ? new Date().toISOString() : null,
          validated_by: args.isValidated ? (user?.id ?? null) : null,
        })
        .eq("id", args.periodId);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["dossier", vars.dossierId, "career-periods"],
      });
    },
  });
}

/**
 * Mutation : update du dossier (réservé Aon interne ou salarié sur son propre).
 */
export function useUpdateDossier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      dossierId,
      patch,
    }: {
      dossierId: string;
      patch: DossierUpdate;
    }): Promise<Dossier> => {
      const { data, error } = await supabase
        .from("dossiers")
        .update(patch)
        .eq("id", dossierId)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dossier.byId(data.id) });
      // « dossier.mine » alimente aussi les verrous côté salarié (B6) : sans
      // cela un « Valider mes souhaits » laisserait le document verrouillé.
      void queryClient.invalidateQueries({ queryKey: ["dossier", "mine"] });
    },
  });
}

// =============================================================================
// Surcotisation employeur (C6) — saisie réservée aux internes Aon
// =============================================================================
// Table non typée (générée ultérieurement) : client non typé comme pour
// dossier_career_periods. RLS : internes Aon uniquement, le salarié et le
// DRH n'y accèdent pas.
export interface DossierSurcotisation {
  id: string;
  dossier_id: string;
  career_period_id: string | null;
  periode_debut: string;
  periode_fin: string | null;
  code_caisse: string;
  taux_ta: number | null;
  taux_tb: number | null;
  taux_tc: number | null;
  taux_activite: number | null;
  type_taux: string | null;
  commentaire: string | null;
  created_by: string | null;
  created_at: string;
}

export function useDossierSurcotisations(dossierId: string | undefined) {
  return useQuery({
    queryKey: dossierId
      ? (["dossier", dossierId, "surcotisations"] as const)
      : (["dossier", "none", "surcotisations"] as const),
    queryFn: async (): Promise<DossierSurcotisation[]> => {
      if (!dossierId) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = supabase.from.bind(supabase) as unknown as (name: string) => any;
      const { data, error } = await client("dossier_surcotisations")
        .select("*")
        .eq("dossier_id", dossierId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as DossierSurcotisation[];
    },
    enabled: Boolean(dossierId),
  });
}

export function useCreateDossierSurcotisation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (args: {
      dossierId: string;
      values: Omit<DossierSurcotisation, "id" | "dossier_id" | "created_by" | "created_at">;
    }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = supabase.from.bind(supabase) as unknown as (name: string) => any;
      const { data, error } = await client("dossier_surcotisations")
        .insert({
          dossier_id: args.dossierId,
          created_by: user?.id ?? null,
          ...args.values,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data as DossierSurcotisation;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["dossier", vars.dossierId, "surcotisations"],
      });
    },
  });
}

export function useDeleteDossierSurcotisation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { dossierId: string; id: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = supabase.from.bind(supabase) as unknown as (name: string) => any;
      const { error } = await client("dossier_surcotisations").delete().eq("id", args.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ["dossier", vars.dossierId, "surcotisations"],
      });
    },
  });
}
